import { useMemo, useState } from "react";
import { CHANNEL_LABEL, CHANNEL_ORDER, genPreviewText, todayJST } from "../../lib/mkRules";
import type { CreateInitiativeInput, InitiativeChannel, InitiativeType } from "../../types/contract";

interface InitiativeFormModalProps {
  onClose: () => void;
  onSubmit: (input: CreateInitiativeInput) => Promise<void>;
  onValidationError: (message: string) => void;
}

const OWNER_CHOICES = ["村瀬", "AI", "外注"];

/** README §Screens 3「施策登録モーダル」。必須4項目＋自動生成プレビュー。 */
export function InitiativeFormModal({ onClose, onSubmit, onValidationError }: InitiativeFormModalProps) {
  const today = todayJST();
  const [name, setName] = useState("");
  const [channel, setChannel] = useState<InitiativeChannel>("line");
  const [type, setType] = useState<InitiativeType>("oneoff");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [owner, setOwner] = useState("村瀬");
  const [purpose, setPurpose] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const preview = useMemo(() => genPreviewText(channel, startDate, endDate), [channel, startDate, endDate]);

  const handleEndDateChange = (v: string) => {
    setEndDate(v && v < startDate ? startDate : v);
  };

  const handleStartDateChange = (v: string) => {
    setStartDate(v);
    if (endDate && endDate < v) setEndDate(v);
  };

  const submit = async () => {
    if (!name.trim()) {
      onValidationError("施策名を入力してください");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        channel,
        type,
        start_date: startDate,
        end_date: endDate < startDate ? startDate : endDate,
        owner,
        purpose: purpose.trim() || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="mk-modal-box" onClick={(e) => e.stopPropagation()}>
        <h3 className="mk-modal-title">施策を登録</h3>
        <p className="mk-modal-sub">必須は4項目。残りはAIが補完します。</p>

        <div className="mk-form-grid">
          <div className="mk-form-field mk-span-2">
            <label>
              施策名 <span className="mk-required">*</span>
            </label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="例：9/4販売開始 Meta広告" />
          </div>

          <div className="mk-form-field">
            <label>
              チャネル <span className="mk-required">*</span>
            </label>
            <select value={channel} onChange={(e) => setChannel(e.target.value as InitiativeChannel)}>
              {CHANNEL_ORDER.map((c) => (
                <option key={c} value={c}>
                  {CHANNEL_LABEL[c]}
                </option>
              ))}
            </select>
          </div>

          <div className="mk-form-field">
            <label>施策タイプ</label>
            <select value={type} onChange={(e) => setType(e.target.value as InitiativeType)}>
              <option value="oneoff">単発</option>
              <option value="recurring">定期</option>
            </select>
          </div>

          <div className="mk-form-field">
            <label>
              開始日 <span className="mk-required">*</span>
            </label>
            <input type="date" value={startDate} onChange={(e) => handleStartDateChange(e.target.value)} />
          </div>

          <div className="mk-form-field">
            <label>
              終了日 <span className="mk-required">*</span>
            </label>
            <input type="date" value={endDate} onChange={(e) => handleEndDateChange(e.target.value)} />
          </div>

          <div className="mk-form-field">
            <label>担当</label>
            <select value={owner} onChange={(e) => setOwner(e.target.value)}>
              {OWNER_CHOICES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>

          <div className="mk-form-field mk-span-2">
            <label>目的・仮説（任意）</label>
            <textarea rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
          </div>

          <div className="mk-form-preview">{preview}</div>
        </div>

        <div className="mk-modal-footer">
          <button className="btn" onClick={onClose} disabled={submitting}>
            キャンセル
          </button>
          <button className="mk-btn-dark" style={{ padding: "7px 16px", borderRadius: 8 }} onClick={submit} disabled={submitting}>
            {submitting ? "登録中…" : "登録する"}
          </button>
        </div>
      </div>
    </div>
  );
}
